/**
 * React hook for accessing and updating wizard state
 * Auto-saves changes with debouncing
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { wizardStore } from '@/stores/wizardStore';
import type { WizardState, WizardEnvironment, WizardPersona, WizardModelSelection, WizardProgress } from '@/stores/wizardStore';
import { saveWizardState, loadWizardState } from '@/lib/wizardPersistence';

function debounce<T extends (...args: any[]) => void>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

export function useWizardState() {
  const [state, setState] = useState<WizardState>(wizardStore.getState());
  const [isLoading, setIsLoading] = useState(true);

  // Subscribe to store updates
  useEffect(() => {
    const unsubscribe = wizardStore.subscribe(setState);
    return () => unsubscribe();
  }, []);

  // Load initial state with migration
  useEffect(() => {
    const loadInitialState = async () => {
      try {
        const loaded = await loadWizardState();
        if (loaded) {
          // Migration: If old structure exists, convert it
          const env = loaded.environment as any;
          if (env.industry !== undefined || env.companySize !== undefined || env.aiUsageLevel !== undefined) {
            // Convert old structure to new structure
            const migrated = {
              ...loaded,
              environment: {
                regulatorySensitivity: env.regulatorySensitivity || 'medium',
                latencyTolerance: env.latencyTolerance || 'balanced',
                totalActiveUsersPerDay: env.totalActiveUsersPerDay || 1000,
                concurrentUsers: env.concurrentUsers || 2000,
                turns: env.turns || 3,
                batch: env.batch || 256,
                precision: env.precision || 8,
              },
              // Preserve personas if they exist
              personas: loaded.personas || [],
            };
            wizardStore.loadState(migrated as WizardState);
          } else {
            wizardStore.loadState(loaded);
          }
        }
      } catch (error) {
        console.error('Failed to load wizard state:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadInitialState();
  }, []);

  // Auto-save with debounce
  const debouncedSave = useMemo(
    () =>
      debounce((s: WizardState) => {
        saveWizardState(s).catch(console.error);
      }, 1000),
    []
  );

  useEffect(() => {
    if (!isLoading) {
      debouncedSave(state);
    }
  }, [state, debouncedSave, isLoading]);

  const updateEnvironment = useCallback((env: Partial<WizardEnvironment>) => {
    wizardStore.updateEnvironment(env);
  }, []);

  const setPersonas = useCallback((personas: WizardPersona[]) => {
    wizardStore.setPersonas(personas);
  }, []);

  const addPersona = useCallback((persona: WizardPersona) => {
    wizardStore.addPersona(persona);
  }, []);

  const updatePersona = useCallback((id: string, updates: Partial<WizardPersona>) => {
    wizardStore.updatePersona(id, updates);
  }, []);

  const removePersona = useCallback((id: string) => {
    wizardStore.removePersona(id);
  }, []);

  const updateModelSelections = useCallback((selections: Partial<WizardModelSelection>) => {
    wizardStore.updateModelSelections(selections);
  }, []);

  const toggleModelSelection = useCallback((modelId: string) => {
    wizardStore.toggleModelSelection(modelId);
  }, []);

  const attachModelToPersonas = useCallback((modelId: string, personaIds: string[]) => {
    wizardStore.attachModelToPersonas(modelId, personaIds);
  }, []);

  const updateProgress = useCallback((progress: Partial<WizardProgress>) => {
    wizardStore.updateProgress(progress);
  }, []);

  const reset = useCallback(() => {
    wizardStore.reset();
  }, []);

  return {
    state,
    isLoading,
    updateEnvironment,
    setPersonas,
    addPersona,
    updatePersona,
    removePersona,
    updateModelSelections,
    toggleModelSelection,
    attachModelToPersonas,
    updateProgress,
    reset,
  };
}
